import React from "react";
import { Box, IconButton, Tooltip } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import color from "../../Constants/colors";

interface MarkdownCodeProps {
  inline?: boolean;
  className?: string;
  children?: React.ReactNode;
  variant?: "default" | "chatUi";
}

const getCodeText = (children: React.ReactNode) => {
  if (Array.isArray(children)) {
    return children.join("");
  }
  if (children === undefined || children === null) {
    return "";
  }
  return String(children);
};

const copyToClipboard = (text: string) => {
  if (navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard.writeText(text).catch(() => {});
    return;
  }

  const textArea = document.createElement("textarea");
  textArea.value = text;
  textArea.style.position = "fixed";
  textArea.style.opacity = "0";
  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();
  try {
    document.execCommand("copy");
  } catch {
    //ignore
  }
  document.body.removeChild(textArea);
};

const MarkdownCode: React.FC<MarkdownCodeProps> = ({
  inline,
  className,
  children,
  variant = "default",
  ...props
}) => {
  if (variant === "default" && inline) {
    return (
      <Box
        component="code"
        sx={{
          backgroundColor: color.secondaryDark,
          padding: "0.2rem 0.4rem",
          borderRadius: "0.25rem",
          fontSize: "0.85rem",
          fontFamily: "monospace",
          color: color.black,
        }}
        {...props}
      >
        {children}
      </Box>
    );
  }

  const codeText = getCodeText(children);

  if (variant === "chatUi") {
    return (
      <Box
        sx={{
          position: "relative",
          backgroundColor: color.secondaryDark,
          border: `2px solid ${color.primary_dark}`,
          borderRadius: "0.5rem",
          width: "100%",
        }}
      >
        <IconButton
          onClick={() => copyToClipboard(codeText)}
          sx={{
            position: "absolute",
            top: "8px",
            right: "8px",
            color: color.primary_dark,
            backgroundColor: color.white,
            borderRadius: "50%",
            padding: "4px",
            cursor: "pointer",
            "&:hover": {
              backgroundColor: color.primary_dark,
              color: color.white,
            },
          }}
        >
          <ContentCopyIcon sx={{ fontSize: "1rem" }} />
        </IconButton>
        <Box
          sx={{
            position: "relative",
            color: color.black,
            fontSize: {
              xl: "0.875rem",
              md: "0.875rem",
              sm: "0.875rem",
              xs: "0.6rem",
            },
            padding: "1rem",
            overflowX: "auto",
            wordWrap: "break-word",
            whiteSpace: "pre-wrap",
            lineHeight: "20px",
            width: "90%",
          }}
        >
          <code className={className} {...props}>
            {children}
          </code>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: "relative",
        backgroundColor: color.secondaryDark,
        border: `2px solid ${color.primary_dark}`,
        borderRadius: "0.5rem",
        width: "100%",
        margin: "0.5rem 0",
      }}
    >
      <Tooltip title="Copy code" arrow>
        <IconButton
          onClick={() => copyToClipboard(codeText)}
          sx={{
            position: "absolute",
            top: "8px",
            right: "8px",
            color: color.primary_dark,
            backgroundColor: color.white,
            borderRadius: "50%",
            padding: "4px",
            cursor: "pointer",
            zIndex: 1,
            "&:hover": {
              backgroundColor: color.primary_dark,
              color: color.white,
            },
          }}
        >
          <ContentCopyIcon sx={{ fontSize: "1rem" }} />
        </IconButton>
      </Tooltip>
      <Box
        sx={{
          color: color.black,
          fontSize: "0.85rem",
          padding: "1rem",
          paddingRight: "3rem",
          overflowX: "auto",
          wordWrap: "break-word",
          whiteSpace: "pre-wrap",
          lineHeight: "1.5",
          fontFamily: "monospace",
        }}
      >
        <code className={className} {...props}>
          {children}
        </code>
      </Box>
    </Box>
  );
};

export default MarkdownCode;
